import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Download, FileText } from "lucide-react";
import { EtsyOrder } from "@/types/etsy";
import { exportOrdersToCSV } from "@/utils/csvExport";
import { useToast } from "@/hooks/use-toast";

interface ExportDialogProps {
  orders: EtsyOrder[];
  children: React.ReactNode;
}

interface ColumnConfig {
  key: keyof EtsyOrder | 'formattedAddress' | 'itemTitle' | 'itemQuantity';
  label: string;
  enabled: boolean;
}

const ExportDialog = ({ orders, children }: ExportDialogProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const { toast } = useToast();

  const [columns, setColumns] = useState<ColumnConfig[]>([
    { key: 'receiptId', label: 'Receipt ID', enabled: true },
    { key: 'customerName', label: 'Customer Name', enabled: true },
    { key: 'customerEmail', label: 'Customer Email', enabled: true },
    { key: 'productName', label: 'Product Name', enabled: true },
    { key: 'itemTitle', label: 'Item Title (for multi-item orders)', enabled: false },
    { key: 'itemQuantity', label: 'Item Quantity (for multi-item orders)', enabled: false },
    { key: 'formattedAddress', label: 'Full Address', enabled: true },
    { key: 'city', label: 'City', enabled: false },
    { key: 'zip', label: 'ZIP Code', enabled: false },
    { key: 'country', label: 'Country', enabled: true },
    { key: 'date', label: 'Order Date', enabled: true },
    { key: 'status', label: 'Order Status', enabled: true },
    { key: 'subtotalAmount', label: 'Net Revenue', enabled: true },
    { key: 'totalAmount', label: 'Total Amount', enabled: true },
    { key: 'currencyCode', label: 'Currency', enabled: true },
    { key: 'trackingId', label: 'Tracking ID', enabled: false },
  ]);

  const toggleColumn = (index: number) => {
    const newColumns = [...columns];
    newColumns[index].enabled = !newColumns[index].enabled;
    setColumns(newColumns);
  };

  const selectAllColumns = () => {
    setColumns(columns.map(col => ({ ...col, enabled: true })));
  };

  const deselectAllColumns = () => {
    setColumns(columns.map(col => ({ ...col, enabled: false })));
  };

  const handleExport = async () => {
    const enabledColumns = columns.filter(col => col.enabled);
    
    if (orders.length === 0) {
      toast({
        title: "No Data",
        description: "No orders available to export",
        variant: "destructive",
      });
      return;
    }

    if (enabledColumns.length === 0) {
      toast({
        title: "No Columns Selected",
        description: "Please select at least one column to export",
        variant: "destructive",
      });
      return;
    }

    setIsExporting(true);
    
    try {
      exportOrdersToCSV(orders, enabledColumns);
      
      // Calculate total rows (including duplicates for multi-item orders)
      const totalRows = orders.reduce((sum, order) => {
        return sum + (order.items && order.items.length > 1 ? order.items.length : 1);
      }, 0);
      
      toast({
        title: "Export Successful",
        description: `${totalRows} rows exported as CSV with ${enabledColumns.length} columns (orders with multiple items are duplicated)`,
      });
      
      setIsOpen(false);
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: "Export Failed",
        description: "Failed to export orders. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const enabledColumnsCount = columns.filter(col => col.enabled).length;

  // Calculate total export rows
  const totalExportRows = orders.reduce((sum, order) => {
    return sum + (order.items && order.items.length > 1 ? order.items.length : 1);
  }, 0);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Export Orders to CSV</DialogTitle>
        </DialogHeader>
        <div className="space-y-6 py-4">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Select Columns to Export</Label>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={selectAllColumns}
                  type="button"
                >
                  Select All
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={deselectAllColumns}
                  type="button"
                >
                  Deselect All
                </Button>
              </div>
            </div>
            
            <div className="grid grid-cols-1 gap-3 max-h-60 overflow-y-auto border rounded-lg p-3">
              {columns.map((column, index) => (
                <div key={String(column.key)} className="flex items-center space-x-3">
                  <Checkbox
                    id={`column-${String(column.key)}`}
                    checked={column.enabled}
                    onCheckedChange={() => toggleColumn(index)}
                  />
                  <Label 
                    htmlFor={`column-${String(column.key)}`} 
                    className="cursor-pointer text-sm"
                  >
                    {column.label}
                  </Label>
                </div>
              ))}
            </div>
          </div>
          
          <div className="bg-blue-50 p-3 rounded-lg">
            <div className="text-sm text-blue-800">
              <strong>{orders.length}</strong> orders will be exported as <strong>{totalExportRows}</strong> rows with <strong>{enabledColumnsCount}</strong> column{enabledColumnsCount !== 1 ? 's' : ''} as CSV format.
              {totalExportRows > orders.length && (
                <div className="mt-1 text-xs">
                  * Orders with multiple items will create separate rows for each item
                </div>
              )}
            </div>
          </div>
          
          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleExport}
              disabled={isExporting || orders.length === 0 || enabledColumnsCount === 0}
            >
              <Download className="mr-2 h-4 w-4" />
              {isExporting ? "Exporting..." : "Export CSV"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ExportDialog;
